/**
 * Pure reconcile-authority decision for HISTORY_UPDATED's replace-in-place
 * branch (useAssistantConnection.ts).
 *
 * Background: during a voice response the backend emits `history_updated`
 * snapshots carrying the assistant item with a PARTIAL transcript within
 * ~1s of response start (live-verified: 27 chars at agent_start, the full
 * 757 chars only ~11s later at audio_end). Voice streaming now rides the
 * SAME message-store pipeline text mode uses (appendAssistantDelta —
 * upsert by item_id, `streaming: true`) — so without this rule, a
 * mid-response snapshot would stomp a message the delta stream is actively
 * growing, visibly retracting in-flight text and clearing its caret
 * mid-word.
 *
 * The SDK's history items carry `status`: 'in_progress' | 'completed' |
 * 'incomplete' (the backend forwards `item.model_dump(mode='json')`
 * verbatim).
 *
 * Rule:
 *  - existing message is mid-stream (`streaming: true`) AND the incoming
 *    item is STILL in progress (`status === 'in_progress'`) → SKIP. The
 *    delta stream owns the content; the snapshot must not touch it.
 *  - anything else — status 'completed', or 'incomplete' (barge-in
 *    truncation is final for that response, so it IS authoritative),
 *    or a missing status (older/QA backends that never send
 *    transcript_delta, so no streaming message exists there to protect),
 *    or a non-streaming existing message — the snapshot IS authoritative:
 *    replace it.
 */
export type HistoryReconcileAction = 'skip' | 'replace';

export function resolveHistoryItemReconcile(
  existingStreaming: boolean | undefined,
  itemStatus: unknown
): HistoryReconcileAction {
  return existingStreaming === true && itemStatus === 'in_progress'
    ? 'skip'
    : 'replace';
}
